import { JsonEditor } from "@/common/JsonEditor";
import GraphDataInlineTree from "@imminently/decisively-ui/GraphDataInlineTree";
import Tabs from "@imminently/decisively-ui/Tabs";
import React from "react";

type PayloadGraphNode = Record<string, any>;

interface PayloadGraph {
  nodes: () => string[];
  node: (id: string) => PayloadGraphNode | undefined;
  rootPath?: string | null;
}

interface InteractionDataViewProps {
  data: unknown;
  graph: any;
  rawData?: unknown;
  showRawToggle?: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function findGraphNode(graph: any, key: string, path: string[]): PayloadGraphNode | undefined {
  const direct = graph?.node?.(key);
  if (direct) return direct;

  const pathString = path.join("/");
  const exactPath = graph?.node?.(pathString);
  if (exactPath) return exactPath;

  if (!graph?.nodes || !graph?.node) return undefined;

  for (const nodeId of graph.nodes()) {
    const node = graph.node(nodeId);
    if (!node) continue;
    const nodePath = typeof node.path === "string" ? node.path : "";
    if (node.id === key || nodePath === pathString || nodePath.endsWith(`/${key}`)) {
      return node;
    }
  }

  return undefined;
}

function getParentPath(data: Record<string, unknown>): string | null {
  const parent = data["@parent"];
  if (typeof parent !== "string") return null;
  const normalisedParent = parent.trim().replace(/^\/+|\/+$/g, "");
  return normalisedParent.length > 0 ? normalisedParent : null;
}

function buildPayloadGraph(data: unknown, graph: any): PayloadGraph | null {
  if (!isPlainObject(data)) return null;

  const nodesById = new Map<string, PayloadGraphNode>();
  const rootPath = getParentPath(data);
  const rootPathSegments = rootPath ? rootPath.split("/") : [];

  const addAttributeNode = (key: string, value: unknown, path: string[]) => {
    if (key === "@id" || key === "@parent") return;
    const pathString = path.join("/");
    const sourceNode = findGraphNode(graph, key, path);
    nodesById.set(pathString, {
      ...(sourceNode ?? {}),
      id: pathString,
      path: pathString,
      description: sourceNode?.description ?? key,
      entity: path.length === 1 ? "global" : (sourceNode?.entity ?? path[path.length - 3] ?? key),
      hidden: false,
      input: value,
      derived: undefined,
      validation: undefined,
    });
  };

  const walkObject = (value: Record<string, unknown>, path: string[]) => {
    for (const [key, entryValue] of Object.entries(value)) {
      if (key === "@id" || key === "@parent") continue;

      if (Array.isArray(entryValue) && entryValue.every(isPlainObject)) {
        entryValue.forEach((item, index) => {
          const itemId = item["@id"] == null || item["@id"] === "" ? String(index + 1) : String(item["@id"]);
          walkObject(item, [...path, key, itemId]);
        });
        continue;
      }

      if (isPlainObject(entryValue) && !findGraphNode(graph, key, [...path, key])) {
        walkObject(entryValue, [...path, key]);
        continue;
      }

      addAttributeNode(key, entryValue, [...path, key]);
    }
  };

  walkObject(data, rootPathSegments);

  if (nodesById.size === 0) return null;

  return {
    nodes: () => Array.from(nodesById.keys()),
    node: (id: string) => nodesById.get(id),
    rootPath,
  };
}

function buildPayloadGraphs(data: unknown, graph: any): PayloadGraph[] {
  if (Array.isArray(data)) {
    return data
      .map((entry) => buildPayloadGraph(entry, graph))
      .filter((entry): entry is PayloadGraph => entry != null);
  }

  const payloadGraph = buildPayloadGraph(data, graph);
  return payloadGraph ? [payloadGraph] : [];
}

export function stringifyJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return JSON.stringify({ data: value }, null, 2);
  }
}

function PayloadGraphTree({ payloadGraph }: { payloadGraph: PayloadGraph }) {
  return (
    <GraphDataInlineTree
      graph={payloadGraph}
      showValues
      showValidationState={false}
      preserveRoot
      childNodesLast
      rootPath={payloadGraph.rootPath}
      className="h-full min-h-[520px]"
    />
  );
}

function PayloadGraphTabs({ payloadGraphs }: { payloadGraphs: PayloadGraph[] }) {
  const [selectedUpdate, setSelectedUpdate] = React.useState("0");

  React.useEffect(() => {
    setSelectedUpdate("0");
  }, [payloadGraphs]);

  if (payloadGraphs.length === 1) {
    return <PayloadGraphTree payloadGraph={payloadGraphs[0]} />;
  }

  return (
    <Tabs
      value={selectedUpdate}
      onValueChange={setSelectedUpdate}
      className="flex h-full min-h-0 flex-col"
    >
      <Tabs.List className="border-b border-border px-3 pt-2">
        {payloadGraphs.map((_, index) => (
          <Tabs.Trigger
            key={index}
            value={String(index)}
          >
            Update {index + 1}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {payloadGraphs.map((payloadGraph, index) => (
        <Tabs.Content
          key={index}
          value={String(index)}
          className="min-h-0 flex-1 overflow-auto p-0"
        >
          <PayloadGraphTree payloadGraph={payloadGraph} />
        </Tabs.Content>
      ))}
    </Tabs>
  );
}

export function InteractionDataView({ data, graph, rawData = data, showRawToggle = true }: InteractionDataViewProps) {
  const [view, setView] = React.useState<"business" | "raw">("business");
  const payloadGraphs = React.useMemo(() => buildPayloadGraphs(data, graph), [data, graph]);

  React.useEffect(() => {
    setView("business");
  }, [data]);

  if (payloadGraphs.length === 0) {
    return (
      <JsonEditor
        className="h-full min-h-[520px]"
        value={stringifyJson(rawData)}
        disabled
        withCopy
      />
    );
  }

  const businessView = <PayloadGraphTabs payloadGraphs={payloadGraphs} />;

  if (!showRawToggle) {
    return <div className="h-full min-h-0 overflow-auto">{businessView}</div>;
  }

  return (
    <Tabs
      value={view}
      onValueChange={(nextView) => setView(nextView as "business" | "raw")}
      className="flex h-full min-h-0 flex-col"
    >
      <Tabs.List className="border-b border-border">
        <Tabs.Trigger value="business">Business view</Tabs.Trigger>
        <Tabs.Trigger value="raw">Raw JSON</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content
        value="business"
        className="min-h-0 flex-1 overflow-auto p-0"
      >
        {businessView}
      </Tabs.Content>
      <Tabs.Content
        value="raw"
        className="min-h-0 flex-1 overflow-auto p-0"
      >
        <JsonEditor
          className="h-full min-h-[520px]"
          value={stringifyJson(rawData)}
          disabled
          withCopy
        />
      </Tabs.Content>
    </Tabs>
  );
}
